/**
 * Select.
 *
 * A native select, restyled. The native control carries the platform's own
 * keyboard map, its mobile picker and its screen reader behavior for free, and
 * nothing in this product's option lists is worth reimplementing that for.
 */

import type { SelectHTMLAttributes } from 'react';
import { useId } from 'react';
import { FieldFrame, describedBy } from './Field';
import '../../styles/components/field.css';
import '../../styles/components/select.css';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  options: SelectOption[];
  help?: string;
  error?: string;
  id?: string;
}

export function Select({ label, options, help, error, id, disabled, ...rest }: SelectProps) {
  const generated = useId();
  const controlId = id ?? generated;

  return (
    <FieldFrame label={label} controlId={controlId} help={help} error={error} disabled={disabled}>
      <div className="select">
        <select
          {...rest}
          id={controlId}
          className="field__select"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(controlId, help, error)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="select__chevron" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
            <path
              d="M4 6.5 8 10.5 12 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </FieldFrame>
  );
}
